<script setup lang="ts">
import {
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';

import { AppStars, AppRouterLink } from '@libs/ui';

import type {
  HotelSelectionCardContentData,
  HotelSelectionCardContext,
} from '../models';

import HotelSelectionAmenities from './HotelSelectionAmenities.vue';

interface HotelSelectionCardContentProps {
  data: HotelSelectionCardContentData;
  context: HotelSelectionCardContext;
}

const props = defineProps<HotelSelectionCardContentProps>();

const routerStore = useRouterStore();
const statsigEventsStore = useStatsigEventsStore();

function onNavigationTriggered(): void {
  statsigEventsStore.logHotelSelectionListCardClicked();
}
</script>

<template>
  <div class="hotel-selection-card-content">
    <div class="upper-section">
      <AppRouterLink
        :link="
          routerStore.getHotelDetailsUrlInHotelSelection(
            context.ingredientIndex,
            context.hotelCode
          )
        "
        font="font-label-xl-bold"
        color="color-primary-20"
        class="name"
        @navigation-triggered="onNavigationTriggered"
      >
        {{ props.data.title }}
      </AppRouterLink>
      <div class="location">{{ props.data.subtitle }}</div>
      <AppStars :amount="props.data.stars" size="18" />
      <HotelSelectionAmenities :amenities="props.data.amenities" />
      <ul>
        <li v-for="content in props.data.content" :key="content" class="item">
          <span class="item-text">
            {{ content }}
          </span>
        </li>
      </ul>
    </div>
    <div class="bottom-section">
      <span class="price">{{ props.data.price }}</span>
      <span v-if="props.data.freeCancellation" class="cancellation-policies">
        {{ $t('filter.rules.free_cancellation') }}
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-card-content {
  padding: var(--spacing-12);
  gap: var(--spacing-8);
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  justify-content: space-between;

  .upper-section {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);

    .name {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .location {
      font: var(--font-body-s-light);
      text-transform: capitalize;
      color: var(--color-secondary-60);
      display: -webkit-box;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    ul {
      margin: 0 0 0 var(--spacing-4);
      padding-top: var(--spacing-8);
      padding-left: var(--spacing-16);
      list-style: none;

      .item {
        font: var(--font-label-s-light);
        color: var(--color-secondary-60);
        position: relative;

        // NOTE: Standard list-style is broken on mobile,
        // when using overflow + ellipsis on list element.
        // Hence custom dot element
        &::before {
          content: '';
          width: 3px;
          height: 3px;
          border-radius: 50%;
          position: absolute;
          background: var(--color-secondary-60);
          left: -9px;
          top: 50%;
          transform: translateY(-50%);
        }

        .item-text {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          line-clamp: 1;
          -webkit-box-orient: vertical;
          word-break: break-all;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        &:not(:last-child) {
          margin-bottom: var(--spacing-4);
        }
      }
    }
  }

  .bottom-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    flex-direction: row-reverse;

    .cancellation-policies {
      font: var(--font-label-s-bold);
    }

    .price {
      font: var(--font-label-xxl-bold);
    }
  }
}
</style>
